import AuthManager from "Common/AuthManager";
import ServiceWrapper from "Services/ServiceWrapper";
import AppSettings from "Settings/AppSettings";

const apiBaseUrl = AppSettings.GMS_BACKEND_API_URL;

export const updateMappingData = async (payload: TDataMappingPayload, channelId: string,) => {
    try {
        const endPoint = `datamapping/${channelId}`;
        const response = await ServiceWrapper.doPatch(
            apiBaseUrl + endPoint,
            payload,
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.log("updateMapping ~ error:", error);
        return null;
    }
};

// ===== SPLIT API ENDPOINTS =====

/**
 * Build query string from request parameters (pagination, filters, search)
 */
const buildQueryString = (params?: Record<string, any>): string => {
    if (!params) return '';

    const queryParams = new URLSearchParams();

    // Add pagination params
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.page_size !== undefined) queryParams.append('page_size', params.page_size.toString());
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);

    // Add search param
    if (params.search) queryParams.append('search', params.search);

    // Add filter params (boolean)
    if (params.is_considered !== undefined) {
        queryParams.append('is_considered', params.is_considered.toString());
    }
    if (params.is_reviewed !== undefined) {
        queryParams.append('is_reviewed', params.is_reviewed.toString());
    }

    // Add array filter params (for attributes)
    if (params.attribute && Array.isArray(params.attribute) && params.attribute.length > 0) {
        params.attribute.forEach((val: string) => queryParams.append('attribute', val));
    }
    if (params.mapped_attribute && Array.isArray(params.mapped_attribute) && params.mapped_attribute.length > 0) {
        params.mapped_attribute.forEach((val: string) => queryParams.append('mapped_attribute', val));
    }

    // Add array filter params (for attribute values)
    if (params.attribute_value && Array.isArray(params.attribute_value) && params.attribute_value.length > 0) {
        params.attribute_value.forEach((val: string) => queryParams.append('attribute_value', val));
    }
    if (params.mapped_attribute_value && Array.isArray(params.mapped_attribute_value) && params.mapped_attribute_value.length > 0) {
        params.mapped_attribute_value.forEach((val: string) => queryParams.append('mapped_attribute_value', val));
    }

    // Add array filter params (for categories)
    if (params.reporting_category && Array.isArray(params.reporting_category) && params.reporting_category.length > 0) {
        params.reporting_category.forEach((val: string) => queryParams.append('reporting_category', val));
    }
    if (params.mapped_category && Array.isArray(params.mapped_category) && params.mapped_category.length > 0) {
        params.mapped_category.forEach((val: string) => queryParams.append('mapped_category', val));
    }

    const query = queryParams.toString();
    return query ? `?${query}` : '';
};

/**
 * Get category mappings for a channel (with optional pagination, filters, and search)
 */
export const getCategoryMappings = async (
    channelId: string,
    params?: CategoryRequestParams
) => {
    try {
        const query = buildQueryString(params);
        const endPoint = `datamapping/${channelId}/categories${query}`;
        const response = await ServiceWrapper.doGet(
            apiBaseUrl + endPoint,
            null,
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.error("getCategoryMappings ~ error:", error);
        return null;
    }
};

/**
 * Get attribute mappings for a channel (with optional pagination, filters, and search)
 */
export const getAttributeMappings = async (
    channelId: string,
    params?: AttributeRequestParams
) => {
    try {
        const query = buildQueryString(params);
        const endPoint = `datamapping/${channelId}/attributes${query}`;
        const response = await ServiceWrapper.doGet(
            apiBaseUrl + endPoint,
            null,
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.error("getAttributeMappings ~ error:", error);
        return null;
    }
};

/**
 * Get attributes filtered by their attribute value mappings
 * Used in Attribute Values tab to show attributes that have attribute values matching filter criteria
 */
export const getAttributeValueFilteredAttributes = async (
    channelId: string,
    params?: AttributeRequestParams
) => {
    try {
        const query = buildQueryString(params);
        const endPoint = `datamapping/${channelId}/attributevalue-attributes${query}`;
        const response = await ServiceWrapper.doGet(
            apiBaseUrl + endPoint,
            null,
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.error("getAttributeValueFilteredAttributes ~ error:", error);
        return null;
    }
};

/**
 * Get attribute value mappings under a mapped attribute (Phase 2 — AV docs
 * are keyed on the mapped attribute, not on the incoming one).
 */
export const getAttributeValueMappings = async (
    channelId: string,
    mappedAttribute: string,
    params?: AttributeValueRequestParams
) => {
    try {
        const query = buildQueryString(params);
        const endPoint = `datamapping/${channelId}/mapped-attributes/${encodeURIComponent(mappedAttribute)}/values${query}`;
        const response = await ServiceWrapper.doGet(
            apiBaseUrl + endPoint,
            null,
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.error("getAttributeValueMappings ~ error:", error);
        return null;
    }
};

/**
 * Get ONLY the unique-mapped count for a mapped attribute's values.
 *
 * Split from getAttributeValueMappings: that count is a ~3s distinct unwind+
 * group on large attributes and used to block the data page render. The values
 * table now loads from getAttributeValueMappings while this count is fetched in
 * parallel and fills the "Unique Mapped Attribute Values" badge independently.
 * Pass the SAME filter/search params as the data call so the badge matches the
 * filtered view.
 */
export const getAttributeValueUniqueCount = async (
    channelId: string,
    mappedAttribute: string,
    params?: AttributeValueRequestParams
) => {
    try {
        const query = buildQueryString(params);
        const endPoint = `datamapping/${channelId}/mapped-attributes/${encodeURIComponent(mappedAttribute)}/values/unique-count${query}`;
        const response = await ServiceWrapper.doGet(
            apiBaseUrl + endPoint,
            null,
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.error("getAttributeValueUniqueCount ~ error:", error);
        return null;
    }
};

/**
 * Bulk mark all attribute values as reviewed for a channel.
 * The backend dispatches a long-running job and returns a task_id
 * immediately (ticket 13997 — the synchronous version hit the API gateway
 * timeout on large channels); poll getBulkReviewJobStatus until it finishes.
 */
export const bulkReviewAttributeValues = async (channelId: string) => {
    try {
        const endPoint = `datamapping/${channelId}/attribute-values/bulk-review`;
        const response = await ServiceWrapper.doPost(
            apiBaseUrl + endPoint,
            { confirm: true },
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.error("bulkReviewAttributeValues ~ error:", error);
        return null;
    }
};

/**
 * Poll the status of a background job by its Celery task id.
 * While running: { processing: true }. On completion the backend job's
 * result object is returned as-is ({ status, done, processing, error, data }).
 */
export const getBulkReviewJobStatus = async (taskId: string) => {
    try {
        const endPoint = `job/status/${taskId}`;
        const response = await ServiceWrapper.doGet(
            apiBaseUrl + endPoint,
            null,
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.error("getBulkReviewJobStatus ~ error:", error);
        return null;
    }
};

const BULK_REVIEW_POLL_MS = 3000;
const BULK_REVIEW_MAX_POLL_FAILURES = 5;

/**
 * Poll the bulk-review job until it leaves the processing state.
 * Returns the job's final result object, or null when polling itself failed
 * BULK_REVIEW_MAX_POLL_FAILURES times in a row (the job keeps running
 * server-side regardless — only the tracking is lost).
 */
export const pollBulkReviewJob = async (taskId: string) => {
    let consecutiveFailures = 0;
    for (;;) {
        const status = await getBulkReviewJobStatus(taskId);
        if (!status) {
            consecutiveFailures += 1;
            if (consecutiveFailures >= BULK_REVIEW_MAX_POLL_FAILURES) {
                return null;
            }
        } else {
            consecutiveFailures = 0;
            if (!status.processing) {
                return status;
            }
        }
        await new Promise((resolve) => setTimeout(resolve, BULK_REVIEW_POLL_MS));
    }
};

/**
 * Get category dropdown options for category mapping tab with pagination support
 * Returns minimal data needed for attribute and attribute-value dropdowns
 * Only returns considered & reviewed items
 *
 * @param channelId - Channel identifier
 * @param attributeName - Optional: If provided, returns attribute values for this attribute only
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 40)
 * @param search - Search term for filtering (default: '')
 */
export const getCategoryDropdownOptions = async (
    channelId: string,
    attributeName?: string,
    page: number = 1,
    limit: number = 40,
    search: string = ''
) => {
    try {
        const queryParams = new URLSearchParams();
        if (attributeName) {
            queryParams.append('attribute_name', attributeName);
        }
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());
        if (search) {
            queryParams.append('search', search);
        }

        const endPoint = `datamapping/${channelId}/category-dropdown-options?${queryParams.toString()}`;
        const response = await ServiceWrapper.doGet(
            apiBaseUrl + endPoint,
            null,
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.error("getCategoryDropdownOptions ~ error:", error);
        return null;
    }
};

/**
 * Industry-scoped Neo4j taxonomy options used by the Attribute / Attribute-Value
 * Mapping "Mapped" dropdowns. Separate from getCategoryDropdownOptions because
 * the Category tab's dropdown intentionally lists already-reviewed mappings.
 */
export const getMappingDropdownOptions = async (
    channelId: string,
    attributeName?: string,
    page: number = 1,
    limit: number = 40,
    search: string = ''
) => {
    try {
        const queryParams = new URLSearchParams();
        if (attributeName) {
            queryParams.append('attribute_name', attributeName);
        }
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());
        if (search) {
            queryParams.append('search', search);
        }

        const endPoint = `datamapping/${channelId}/mapping-dropdown-options?${queryParams.toString()}`;
        const response = await ServiceWrapper.doGet(
            apiBaseUrl + endPoint,
            null,
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.error("getMappingDropdownOptions ~ error:", error);
        return null;
    }
};

/**
 * Get filter options for dropdowns (returns all unique values from server)
 */
export const getFilterOptions = async (
    channelId: string,
    tab: MappingTabType,
    attributeName?: string
) => {
    try {
        const queryParams = new URLSearchParams();
        queryParams.append('tab', tab);
        if (tab === 'attributevalue' && attributeName) {
            queryParams.append('attribute_name', attributeName);
        }

        const endPoint = `datamapping/${channelId}/filter-options?${queryParams.toString()}`;
        const response = await ServiceWrapper.doGet(
            apiBaseUrl + endPoint,
            null,
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.error("getFilterOptions ~ error:", error);
        return null;
    }
};

export const getPaginatedFilterOptions = async (
    channelId: string,
    tab: MappingTabType,
    field: 'incoming' | 'mapped',
    page: number = 1,
    search: string = '',
    attributeName?: string
) => {
    try {
        const queryParams = new URLSearchParams();
        queryParams.append('tab', tab);
        queryParams.append('field', field);
        queryParams.append('page', page.toString());
        queryParams.append('limit', '50');
        if (search) {
            queryParams.append('search', search);
        }
        if (tab === 'attributevalue' && attributeName) {
            queryParams.append('attribute_name', attributeName);
        }

        const endPoint = `datamapping/${channelId}/filter-options?${queryParams.toString()}`;
        const response = await ServiceWrapper.doGet(
            apiBaseUrl + endPoint,
            null,
            null,
            AuthManager.getGmsToken()
        );
        return response?.data;
    } catch (error) {
        console.error("getPaginatedFilterOptions ~ error:", error);
        return null;
    }
};